import { Checkbox, FormControlLabel, Radio } from "@mui/material";
import React, { useContext, useEffect, useMemo } from "react";
import { DESSERT_DAYS_PATH, DISCOUNT_STAFF_PATH, mealsPath, menuPath, MODEL_COMPLEX_PATH, SIDE_DAYS_PATH, studentsPath, userOrdersMealsPath, userProfilePath } from "../../functions/src/admin/paths";
import { PORTION_SIZE_NORMAL } from "../../functions/src/api/constants";
import { toDateFromServer } from "../../functions/src/api/dates";
import { canOrderMenuMealsForDay } from "../../functions/src/common/orderDeadlines";
import AllergenPdfDownloadLink from "./AllergenPdfDownloadLink";
import { auth, currentCatering, currentMenu, DataSnapshot } from "../api/firebase";
import { useDatabase, useDatabaseVal, useList, useSnapshotState, useStorageUrl } from "../api/firebaseHooks";
import { getLangValue, langContext } from "../api/Lang";
import { useStrings } from "../common-strings";
import { Meal, MealLine, ModelComplexity, OrderPerson, UserProfile } from "../types";
import { useOrderStrings } from "./order-strings";
import { useOrderDeadlineAt } from "./useOrderDeadlineAt";

export default function SetDayMenuPopUp({ date, mealsList, typesList, recipient, schoolBreak, OrderMealsLists }: SetDayMenuPopUpProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const orderStrings = useOrderStrings();
    const allowMenu = true;

    const orderDate = useMemo(() => toDateFromServer(date), [date]);

    const OrderMealsListsBreak = OrderMealsLists?.filter(orderPerBreak => orderPerBreak.child('schoolBreak').val() === schoolBreak);
    const filteredSnapshot = OrderMealsListsBreak.filter(sMeal => sMeal.child('recipient').val() === recipient && !sMeal.child('isPackage').val());

    const mSnapshot = useDatabase(`${menuPath(currentMenu)}/${date}`);
    const loading = !mSnapshot;
    const menusList = useList(mSnapshot);

    const sBreakfastMeals = useDatabase(mealsPath(currentCatering), ref => ref.orderByChild('courseType').equalTo('breakfast'));
    const breakfastMealsList = useList(sBreakfastMeals)?.filter(sMeal => sMeal.child('order')?.val() >= 0);
    const hasBreakfastItems = breakfastMealsList.length > 0;
    const bLoading = !breakfastMealsList;
    const breakfastMealKeys = breakfastMealsList.map(sMeal => sMeal.key);

    const morningSnackMeals = useDatabase(mealsPath(currentCatering), ref => ref.orderByChild('courseType').equalTo('morningSnack'));
    const morningSnackMealsList = useList(morningSnackMeals)?.filter(sMeal => sMeal.child('order')?.val() >= 0);
    const hasMorningSnackItems = morningSnackMealsList.length > 0;
    const msLoading = !morningSnackMealsList;
    const morningSnackMealKeys = morningSnackMealsList.map(sMeal => sMeal.key);

    // Keys to exclude from set menus
    const excludedMealKeys = ['sideMenu', 'dessertMenu', 'breakfast', 'morningSnack'];
    const numOfSetMenus = menusList?.filter(sMeal => !excludedMealKeys.includes(sMeal.key))?.length;
    const hasSetMenu = numOfSetMenus > 0;

    const sideDish: Meal = mSnapshot?.child('sideMenu')?.val();
    const dessert: Meal = mSnapshot?.child('dessertMenu')?.val();
    const breakfast: Meal = mSnapshot?.child('breakfast')?.val();
    const morningSnack: Meal = mSnapshot?.child('morningSnack')?.val();
    const hasBreakfast = !!breakfast;
    const hasMorningSnack = !!morningSnack;

    const setMenusList = menusList.filter(menu => !excludedMealKeys.includes(menu.key));
    const breakfastList = menusList.filter(menu => menu.key === 'breakfast');
    const morningSnackList = menusList.filter(menu => menu.key === 'morningSnack');

    // Database reference for orders
    const sOrderMealsMain = useDatabase(date ? userOrdersMealsPath(auth.currentUser.uid, date) : null);

    // Auto-select first set menu item when popup opens
    useEffect(() => {
        if (setMenusList.length > 0 && OrderMealsListsBreak && sOrderMealsMain?.ref) {
            const setMenuKeys = setMenusList.map(menu => menu.child('key').val());
            const hasExistingSetMenuOrder = filteredSnapshot.some(sMeal =>
                setMenuKeys.includes(sMeal.child('key').val())
            );

            if (!hasExistingSetMenuOrder) {
                // Auto-add the first set menu item only if no set menu item exists
                const firstSetMenu = setMenusList[0];
                const firstSetMenuKey = firstSetMenu.child('key').val();
                sOrderMealsMain.ref.push({
                    quantity: 1,
                    key: firstSetMenuKey,
                    recipient,
                    schoolBreak,
                    quantitySide: !!sideDish ? 1 : null,
                    quantityDessert: !!dessert ? 1 : null,
                    portionSize: PORTION_SIZE_NORMAL
                });
            }
        }
    }, [setMenusList.length, recipient, schoolBreak, date]);

    // Auto-select first radio button for breakfast and morning snack when popup opens
    useEffect(() => {
        if (sOrderMealsMain?.ref) {
            // Handle breakfast auto-selection
            if (hasBreakfastItems) {
                // Determine which breakfast items are actually being rendered
                const actualBreakfastKeys = hasBreakfast
                    ? breakfastList.map(breakfast => breakfast.child('key').val())
                    : breakfastMealsList.filter(listKey => !excludedMealKeys.includes(listKey.key)).map(listKey => listKey.key);

                const hasExistingBreakfastOrder = filteredSnapshot.some(sMeal =>
                    actualBreakfastKeys.includes(sMeal.child('key').val())
                );

                if (!hasExistingBreakfastOrder && actualBreakfastKeys.length > 0) {
                    // Auto-add the first actually rendered breakfast item
                    sOrderMealsMain.ref.push({
                        quantity: 1,
                        key: actualBreakfastKeys[0],
                        recipient,
                        schoolBreak,
                        portionSize: PORTION_SIZE_NORMAL
                    });
                }
            }

            // Handle morning snack auto-selection
            if (hasMorningSnackItems) {
                // Determine which morning snack items are actually being rendered
                const actualMorningSnackKeys = hasMorningSnack
                    ? morningSnackList.map(morningSnack => morningSnack.child('key').val())
                    : morningSnackMealsList.filter(listKey => !excludedMealKeys.includes(listKey.key)).map(listKey => listKey.key);

                const hasExistingMorningSnackOrder = filteredSnapshot.some(sMeal =>
                    actualMorningSnackKeys.includes(sMeal.child('key').val())
                );

                if (!hasExistingMorningSnackOrder && actualMorningSnackKeys.length > 0) {
                    // Auto-add the first actually rendered morning snack item
                    sOrderMealsMain.ref.push({
                        quantity: 1,
                        key: actualMorningSnackKeys[0],
                        recipient,
                        schoolBreak,
                        portionSize: PORTION_SIZE_NORMAL
                    });
                }
            }
        }
    }, [breakfastMealsList.length, morningSnackMealsList.length, recipient, schoolBreak, date]);

    return <div>

        <div className="header" style={{ marginBottom: '30px', flexFlow: 'row nowrap' }}>
            <div className={'popup-heading-meals'} style={{ marginTop: '0px' }}>{orderStrings.setDayMenuPopupBreakfastHeading}</div>
            <AllergenPdfDownloadLink lang={lang} targetDate={orderDate} label={orderStrings.mealsPopupAllergensList} />
        </div>

        {/* <div className={'menu-description'}><Lang>Περιλαμβάνει 1 χυμό ανάμεικτο & 1 μπουκαλάκι νερό|Includes 1 mixed juice & 1 bottle of water</Lang></div> */}
        {bLoading && commonStrings.loading
            || !hasBreakfastItems &&
            <div className="day-selector" style={{ marginTop: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" style={{ fill: "var(--red_error)" }} /></svg>
                <span className='items-left' style={{ marginLeft: '10px' }}>{orderStrings.setDayMenuPopupSnackNotAvailable}</span>
            </div>
            || hasBreakfast &&
            breakfastList.map(breakfast =>
                <MealPopUpLine key={breakfast.key}
                    date={date}
                    mealKey={breakfast.child('key').val()}
                    meal={breakfast.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === breakfast.child('key').val())[0]}
                    sideDish={null}
                    dessert={null}
                    mealKeys={breakfastList.map(breakfast => breakfast.child('key').val())}
                    isPreSelected={true}
                    isDisabled={true}
                    radioGroupName={`breakfast-${recipient}-${schoolBreak}`}
                />)
            || breakfastMealsList.map(listKey => !excludedMealKeys.includes(listKey.key) &&
                <MealPopUpLine key={listKey.key}
                    date={date}
                    mealKey={listKey.key}
                    meal={listKey.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === listKey.key)[0]}
                    sideDish={sideDish}
                    dessert={dessert}
                    mealKeys={breakfastMealKeys}
                    radioGroupName={`breakfast-${recipient}-${schoolBreak}`}
                />)
        }

        {/* Breakfast: Included items */}
        <div style={{
            backgroundColor: 'var(--lb_green_bgr)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
            gap: '10px',
            marginTop: '20px'
        }}>
            <div style={{
                flex: 1,
                color: 'var(--lb_green)',
                fontSize: '13px',
                fontWeight: 'normal'
            }}>
                {orderStrings.setDayMenuPopupBreakfastIncluded}
            </div>
            <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '8px',
                backgroundImage: 'url(/img-assets/juice.svg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}></div>
            <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '8px',
                backgroundImage: 'url(/img-assets/water-bottle.svg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}></div>
        </div>

        {/* <div className={'divider-line'}></div> */}

        <div className={'popup-heading-meals'}>{orderStrings.setDayMenuPopupMorningSnackHeading}</div>
        {msLoading && commonStrings.loading
            || !hasMorningSnackItems &&
            <div className="day-selector" style={{ marginTop: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" style={{ fill: "var(--red_error)" }} /></svg>
                <span className='items-left' style={{ marginLeft: '10px' }}>{orderStrings.setDayMenuPopupSnackNotAvailable}</span>
            </div>
            || hasMorningSnack &&
            morningSnackList.map(morningSnack =>
                <MealPopUpLine key={morningSnack.key}
                    date={date}
                    mealKey={morningSnack.child('key').val()}
                    meal={morningSnack.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === morningSnack.child('key').val())[0]}
                    sideDish={null}
                    dessert={null}
                    mealKeys={morningSnackList.map(morningSnack => morningSnack.child('key').val())}
                    isPreSelected={true}
                    isDisabled={true}
                    radioGroupName={`morningSnack-${recipient}-${schoolBreak}`}
                />)
            || morningSnackMealsList.map(listKey => !excludedMealKeys.includes(listKey.key) &&
                <MealPopUpLine key={listKey.key}
                    date={date}
                    mealKey={listKey.key}
                    meal={listKey.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === listKey.key)[0]}
                    sideDish={sideDish}
                    dessert={dessert}
                    mealKeys={morningSnackMealKeys}
                    radioGroupName={`morningSnack-${recipient}-${schoolBreak}`}
                />)
        }
        {/* <div className={'divider-line'}></div> */}

        <div className={'popup-heading-meals'}>{orderStrings.setDayMenuPopupLunchHeading}</div>
        {!allowMenu && <div className={'menu-description'} style={{ color: 'var(--red_error)' }}>{orderStrings.mealsPopupPackageOnlyNote}</div>}
        {loading && commonStrings.loading
            || !hasSetMenu &&
            <div className="day-selector" style={{ marginTop: '20px' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14"><path fill="none" d="M0 0h24v24H0z" /><path d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.477 10-10 10zm-1-7v2h2v-2h-2zm0-8v6h2V7h-2z" style={{ fill: "var(--red_error)" }} /></svg>
                <span className='items-left' style={{ marginLeft: '10px' }}>{orderStrings.mealsPopupSetMenusNotYetAvailable}</span>
            </div>
            || setMenusList.map(menu =>
                <MealPopUpLine key={menu.key}
                    date={date}
                    mealKey={menu.child('key').val()}
                    meal={menu.val()}
                    recipient={recipient}
                    schoolBreak={schoolBreak}
                    qSnapshot={filteredSnapshot.filter(sMeal => sMeal.child('key').val() === menu.child('key').val())[0]}
                    sideDish={sideDish}
                    dessert={dessert}
                    mealKeys={setMenusList.map(menu => menu.child('key').val())}
                    radioGroupName={`setMenu-${recipient}-${schoolBreak}`}
                />)
        }

        <div style={{
            backgroundColor: 'var(--lb_green_bgr)',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            padding: '10px',
            gap: '10px',
            marginTop: '20px'
        }}>
            <div style={{
                flex: 1,
                color: 'var(--lb_green)',
                fontSize: '13px',
                fontWeight: 'normal'
            }}>
                {orderStrings.setDayMenuPopupLunchWaterNote}
            </div>
            <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '8px',
                backgroundImage: 'url(/img-assets/water-bottle.svg)',
                backgroundSize: 'cover',
                backgroundPosition: 'center'
            }}></div>
        </div>

        {/* <div className={'divider-line'}></div> */}

    </div>
}

type SetDayMenuPopUpProps = {
    date: string
    mealsList: DataSnapshot<Meal>[];
    typesList: DataSnapshot[];
    recipient: string;
    schoolBreak: string;
    OrderMealsLists: DataSnapshot[]; // Meals of order per recipient and time
}

function MealCategory({ heading, subHeading }: { heading: string, subHeading: string }) {

    return <div>
        <div className='popup-heading-meals'>
            {heading}
        </div>
        <div className={'menu-description'}>
            {subHeading}
        </div>
    </div>

}

function MealPopUpLine({ date, mealKey, meal, recipient, schoolBreak, qSnapshot, sideDish, dessert, mealKeys, isPreSelected = false, isDisabled = false, radioGroupName }: MealPopUpLineProps) {

    const [lang] = useContext(langContext);
    const commonStrings = useStrings();
    const [modelConstants] = useDatabaseVal<ModelComplexity>(MODEL_COMPLEX_PATH);
    const deadlineAt = useOrderDeadlineAt();

    // Convert Firebase Storage URL to download URL
    const imageUrl = useStorageUrl(meal?.imgUrl);

    const [quantity, setQuantity] = useSnapshotState<number>(qSnapshot, 'quantity');

    const [profile] = useDatabaseVal<UserProfile>(userProfilePath(auth.currentUser.uid));
    const { uid } = auth.currentUser;

    // Check if recipient is staff or student using per-person flags
    const students = useDatabase(studentsPath(uid));
    const recipientData: DataSnapshot<OrderPerson> = useDatabase(students ? `${studentsPath(uid)}/${recipient}` : null);
    const recipientPerson = recipientData?.val();
    const recipientIsStaff = !!recipientPerson?.personnel;
    const recipientIsStudent = !!recipientPerson?.student;

    const isPackage: boolean = qSnapshot?.child('isPackage')?.val() || false;

    const staffDiscount: number = useDatabase(DISCOUNT_STAFF_PATH)?.val() || 0;

    const isSetMenu = meal.courseType === 'main';

    const dayNum = toDateFromServer(date).getDay();

    const mealSide = meal?.sideDish?.[lang];
    const sideDay: string = useDatabase(`${SIDE_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const sideMenu: string = !sideDish ? '' : getLangValue(sideDish.name, lang);
    const sideName: string = !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;
    // const sideName: string = meal?.preventSide ? null : !mealSide ? !sideMenu ? sideDay : sideMenu : mealSide;

    const dessertDay: string = useDatabase(`${DESSERT_DAYS_PATH}/${dayNum}/${lang}`)?.val() ?? '';
    const dessertMenu: string = !dessert ? '' : getLangValue(dessert.name, lang);
    const dessertName: string = !dessertMenu ? dessertDay : dessertMenu;
    // const dessertName: string = meal?.preventDessert ? null : !dessertMenu ? dessertDay : dessertMenu;

    const canOrderMealMenu = !isSetMenu || (!!deadlineAt && canOrderMenuMealsForDay(date, modelConstants, deadlineAt)); //|| !isPackage;
    const canOrderSetMenu = !isSetMenu || (modelConstants?.allowPackageMealsParents && recipientIsStudent) || (modelConstants?.allowPackageMealsStaff && recipientIsStaff) || isPackage;
    const cannotOrderMealSetMenu = (!canOrderMealMenu || !canOrderSetMenu);

    const isSetMenuForStudent = recipientIsStudent && isSetMenu;

    const mealLine: MealLine = qSnapshot?.val();

    const sOrderMeals = useDatabase(date && userOrdersMealsPath(uid, date) || null);
    const sOrderMealsList = useList(sOrderMeals);

    // Determine if this is a checkbox that should be auto-selected and disabled
    const isCheckboxDisabled = !isSetMenu && mealKeys?.length === 1;
    const isRadioAutoSelect = !isSetMenu && mealKeys?.length > 1;

    const removeOtherMeals = () => {
        sOrderMealsList?.forEach(orderMeal => {
            const orderMealKey = orderMeal.child('key').val();
            const orderRecipient = orderMeal.child('recipient').val();
            const orderSchoolBreak = orderMeal.child('schoolBreak').val();

            // Remove if it's in the same meal group (same mealKeys) and same recipient/schoolBreak
            if (mealKeys.includes(orderMealKey) &&
                orderRecipient === recipient &&
                orderSchoolBreak === schoolBreak) {
                orderMeal.ref.remove();
            }
        });
    }
    const addMeal = () => {
        if (isSetMenuForStudent)
            sOrderMeals.ref.push({
                quantity: 1,
                key: mealKey,
                recipient,
                schoolBreak,
                quantitySide: !!meal.sideDish ? 1 : null,
                quantityDessert: !!dessert ? 1 : null,
                portionSize: PORTION_SIZE_NORMAL
            });
        else
            sOrderMeals.ref.push({
                quantity: 1,
                key: mealKey,
                recipient,
                schoolBreak,
                portionSize: PORTION_SIZE_NORMAL
            });
    }

    const onSelectMeal = () => {
        if (shouldBeDisabled) return; // Don't allow changes for disabled items

        if (quantity > 0) {
            // If already selected, remove it (uncheck)
            qSnapshot?.ref.remove();
        } else {
            // If not selected, add it (check)
            removeOtherMeals();
            addMeal();
        }
    }

    // Determine checkbox state
    const isChecked = isPreSelected || quantity > 0;
    const shouldBeDisabled = isDisabled || isCheckboxDisabled;

    // Set menu layout (with image card)
    if (isSetMenu) {
        return <div style={{
            marginTop: '15px',
            display: 'flex',
            alignItems: 'center',
            cursor: shouldBeDisabled ? 'default' : 'pointer',
            width: '100%',
            gap: '15px'
        }} onClick={(e) => {
            if (shouldBeDisabled) return;
            const radio = e.currentTarget.querySelector('input[type="radio"]') as HTMLInputElement;
            const checkbox = e.currentTarget.querySelector('input[type="checkbox"]:not([disabled])') as HTMLInputElement;
            const targetInput = radio || checkbox;
            const target = e.target as Element;
            if (targetInput && e.target !== targetInput && !target.closest('.MuiRadio-root') && !target.closest('.MuiCheckbox-root')) {
                onSelectMeal();
            }
        }}>
            {/* Text content container */}
            <div style={{ flex: 1, marginRight: !imageUrl ? '165px' : '0' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                    {mealKeys?.length > 1 &&
                        <Radio
                            checked={isChecked}
                            onChange={onSelectMeal}
                            disabled={shouldBeDisabled}
                            name={radioGroupName}
                            sx={{ marginRight: '10px', padding: 0 }}
                            size="medium"
                        />
                        ||
                        <Checkbox
                            checked={isChecked}
                            onChange={onSelectMeal}
                            disabled={shouldBeDisabled}
                            sx={{
                                marginRight: '10px',
                                padding: 0,
                                '&.Mui-disabled': {
                                    color: 'var(--lb_green)',
                                    opacity: 0.5
                                }
                            }}
                            size="medium"
                        />
                    }

                    <div style={{ flex: 1 }}>
                        <div className={`popup-title ${cannotOrderMealSetMenu && 'opac5' || ''}`} >
                            {getLangValue(meal.name, lang)}
                            <span style={{ marginLeft: '15px' }}>
                                {!!meal.bio && <img className="meal-attr-icons" src="/img-assets/bio.svg" title={commonStrings.bio} />}
                                {!!meal.vegan && <img className="meal-attr-icons" src="/img-assets/vegan.svg" title={commonStrings.vegetarian} />}
                                {!!meal.glutenFree && <img className="meal-attr-icons" src="/img-assets/gluten-free.svg" title={commonStrings.glutenFree} />}
                                {!!meal.frozen && <img className="meal-attr-icons" src="/img-assets/frozen.svg" title={commonStrings.frozen} />}
                            </span>
                        </div>

                        <div className='popup-title popup-descr' style={{ marginBottom: '12px' }}>{getLangValue(meal.description, lang)}</div>

                        {!!sideName &&
                            <div className='popup-title-line-meals selected'>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={true}
                                        checked={true}
                                        sx={{
                                            marginRight: '10px',
                                            padding: 0,
                                            '&.Mui-disabled': {
                                                color: 'var(--lb_green)',
                                                opacity: 0.5
                                            }
                                        }}
                                        size="small"
                                    />}
                                    label={sideName}
                                    sx={{
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            opacity: cannotOrderMealSetMenu ? 0.5 : 1,
                                            fontSize: '14px'
                                        }
                                    }}
                                />
                            </div>
                        }

                        {!!dessertName &&
                            <div className='popup-title-line-meals selected' style={{ marginTop: sideName ? 0 : '12px' }}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={true}
                                        checked={true}
                                        sx={{
                                            marginRight: '10px',
                                            padding: 0,
                                            '&.Mui-disabled': {
                                                color: 'var(--lb_green)',
                                                opacity: 0.5
                                            }
                                        }}
                                        size="small"
                                    />}
                                    label={dessertName}
                                    sx={{
                                        margin: 0,
                                        '& .MuiFormControlLabel-label': {
                                            opacity: cannotOrderMealSetMenu ? 0.5 : 1,
                                            fontSize: '14px'
                                        }
                                    }}
                                />
                            </div>
                        }
                    </div>
                </div>
            </div>

            {/* Food Image container */}
            {imageUrl && (
                <div style={{
                    width: '150px',
                    height: '100px',
                    backgroundColor: 'var(--white)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                }}>
                    <img
                        src={imageUrl}
                        alt={getLangValue(meal.name, lang)}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            borderRadius: '8px'
                        }}
                    />
                </div>
            )}
        </div>
    }

    // Original layout for breakfast, morning snacks, etc.
    return <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'space-between', cursor: shouldBeDisabled ? 'default' : 'pointer' }} onClick={(e) => {
        if (shouldBeDisabled) return;
        const radio = e.currentTarget.querySelector('input[type="radio"]') as HTMLInputElement;
        const checkbox = e.currentTarget.querySelector('input[type="checkbox"]:not([disabled])') as HTMLInputElement;
        const targetInput = radio || checkbox;
        const target = e.target as Element;
        if (targetInput && e.target !== targetInput && !target.closest('.MuiRadio-root') && !target.closest('.MuiCheckbox-root')) {
            onSelectMeal();
        }
    }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            {mealKeys?.length > 1 &&
                <Radio
                    checked={isChecked}
                    onChange={onSelectMeal}
                    disabled={shouldBeDisabled}
                    name={radioGroupName}
                    sx={{ marginRight: '10px', padding: 0 }}
                    size="medium"
                />
                ||
                <Checkbox
                    checked={isChecked}
                    onChange={onSelectMeal}
                    disabled={shouldBeDisabled}
                    sx={{
                        marginRight: '10px',
                        padding: 0,
                        '&.Mui-disabled': {
                            color: 'var(--lb_green)',
                            opacity: 0.5
                        }
                    }}
                    size="medium"
                />
            }
            <span className={`popup-title ${cannotOrderMealSetMenu && 'opac5' || ''}`} style={{ display: 'flex', alignItems: 'center' }}>
                {getLangValue(meal.name, lang)}
                <span style={{ marginLeft: '15px' }}>
                    {!!meal.bio && <img className="meal-attr-icons" src="/img-assets/bio.svg" title={commonStrings.bio} />}
                    {!!meal.vegan && <img className="meal-attr-icons" src="/img-assets/vegan.svg" title={commonStrings.vegetarian} />}
                    {!!meal.glutenFree && <img className="meal-attr-icons" src="/img-assets/gluten-free.svg" title={commonStrings.glutenFree} />}
                    {!!meal.frozen && <img className="meal-attr-icons" src="/img-assets/frozen.svg" title={commonStrings.frozen} />}
                </span>
            </span>
        </div>

        {getLangValue(meal.description, lang) && (
            <div style={{ paddingLeft: mealKeys?.length > 1 ? '42px' : '42px', marginBottom: '10px' }}>
                <div className='popup-title popup-descr'>{getLangValue(meal.description, lang)}</div>
            </div>
        )}

        {/* Centered Food Image */}
        {imageUrl && (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
                <div style={{
                    width: '150px',
                    height: '100px',
                    backgroundColor: 'var(--white)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <img
                        src={imageUrl}
                        alt={getLangValue(meal.name, lang)}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            borderRadius: '8px'
                        }}
                    />
                </div>
            </div>
        )}
    </div>
}
type MealPopUpLineProps = {
    date: string
    mealKey: string
    meal: Meal
    recipient: string
    schoolBreak: string
    qSnapshot: DataSnapshot
    sideDish: Meal
    dessert: Meal
    isSelected?: boolean
    onSelect?: () => void
    selectionType?: 'breakfast' | 'morningSnack' | 'setMenu'
    mealKeys: string[]
    isPreSelected?: boolean
    isDisabled?: boolean
    radioGroupName?: string
}